import { prepareForSlot, type SelectOwnerState } from '@mui/base'
import { clsx } from 'clsx'
import {
  type Range,
  type RangeBoundaries,
} from 'instantsearch.js/es/connectors/range/connectRange'
import {
  type ComponentProps,
  type JSX,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Input, Slider } from 'base-ui'

import { useProductSearchRefinementContext } from '../ProductSearchRefinement'
import { ProductSearchRefinementClearRefinement } from '../ProductSearchRefinementClearRefinement'
import { useProductSearchRefinementStateContext } from '../ProductSearchRefinementStateContext'

import { ProductSearchRefinementRatingInputRating } from './ProductSearchRefinementRatingInputRating'

import styles from './ProductSearchRefinementRatingListboxBody.module.css'

const numberCompare = (numberA: number, numberB: number): number =>
  numberA - numberB

const InputSlot = prepareForSlot(ProductSearchRefinementRatingInputRating)

const RATING_DEFAULT_BOUNDARIES = {
  max: 5,
  min: 1,
} satisfies Range

const getRangeBoundaries = (range: Range): { max: number; min: number } => {
  const {
    max = RATING_DEFAULT_BOUNDARIES.max,
    min = RATING_DEFAULT_BOUNDARIES.min,
  } = range

  return {
    max: Math.min(max, RATING_DEFAULT_BOUNDARIES.max),
    min: Math.max(min, RATING_DEFAULT_BOUNDARIES.min),
  }
}

/**
 * Convert Algolia range parameters into input values for components
 * @param range Possible min/max values
 * @param start Current selected boundaries
 */
const getRangeValue = (
  range: Range,
  start: RangeBoundaries,
): [from: number, to: number] => {
  const { max, min } = getRangeBoundaries(range)

  const from =
    start[0] === undefined || !Number.isFinite(start[0]) ? min : start[0]

  const to =
    start[1] === undefined || !Number.isFinite(start[1]) ? max : start[1]

  return [Math.max(from, min), Math.min(to, max)]
}

const getInputRange = (input: number[]): [number, number] => {
  const [from, to] = input.toSorted(numberCompare)

  return [from, to]
}

type ProductSearchRefinementRatingListboxBodyProps<
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
> = ComponentProps<'div'> & {
  ownerState: SelectOwnerState<OptionValue, Multiple>
}
export const ProductSearchRefinementRatingListboxBody = <
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean,
>(
  props: ProductSearchRefinementRatingListboxBodyProps<OptionValue, Multiple>,
  // eslint-disable-next-line max-lines-per-function -- extracting mobile controls requires prop drilling which conflicts with the context pattern used in this codebase
): JSX.Element => {
  const {
    children,
    className,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- not used for rendering
    ownerState,
    ...restProps
  } = props

  const intl = useIntl()

  const {
    state: { isMobile },
  } = useProductSearchRefinementStateContext()

  const refinementContext = useProductSearchRefinementContext()

  if (!('refinementRange' in refinementContext)) {
    throw new Error('The refinement is not a range refinement.')
  }

  const { refinementRange, refinementRangeOptions } = refinementContext

  const { attribute } = refinementRangeOptions

  const { canRefine, range, refine, start } = refinementRange

  const { max, min } = getRangeBoundaries(range)

  const [inputRangeValue, setInputRangeValue] = useState(() =>
    getRangeValue(range, start),
  )

  const onInputRangeChange = useCallback((value: number[]): void => {
    setInputRangeValue(getInputRange(value))
  }, [])

  const [from, to] = inputRangeValue

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
    setInputRangeValue(getRangeValue(range, start))
  }, [range, start])

  return (
    <div
      className={clsx(
        styles['product-search-refinement-rating-listbox-body'],
        className,
      )}
      {...restProps}
    >
      {isMobile ? (
        <>
          <div className="flex items-center gap-4 self-stretch desktop:gap-1">
            <Input
              className="flex-1"
              disabled={!canRefine}
              onChange={(event) => {
                const fromValue = Number(event.target.value)

                onInputRangeChange([fromValue, to])

                refine([fromValue, to])
              }}
              slotProps={{
                input: {
                  'aria-label': intl.formatMessage({
                    id: 'searchResults.label.rating.min',
                  }),
                },
              }}
              slots={{
                input: InputSlot,
              }}
              value={from}
            />
            <FormattedMessage id="general.label.until" />
            <Input
              className="flex-1"
              disabled={!canRefine}
              onChange={(event) => {
                const toValue = Number(event.target.value)

                onInputRangeChange([from, toValue])

                refine([from, toValue])
              }}
              slotProps={{
                input: {
                  'aria-label': intl.formatMessage({
                    id: 'searchResults.label.rating.max',
                  }),
                },
              }}
              slots={{
                input: InputSlot,
              }}
              value={to}
            />
          </div>

          <Slider
            className="w-full"
            disabled={!canRefine}
            getAriaLabel={(index) =>
              index === 0
                ? intl.formatMessage({ id: 'searchResults.label.rating.min' })
                : intl.formatMessage({ id: 'searchResults.label.rating.max' })
            }
            max={max}
            min={min}
            onChange={(event, value) => {
              if (Array.isArray(value)) {
                onInputRangeChange(value)
              }
            }}
            onChangeCommitted={(event, value) => {
              if (Array.isArray(value)) {
                refine(getInputRange(value))
              }
            }}
            step={1}
            value={[from, to]}
          />
        </>
      ) : (
        children
      )}

      <ProductSearchRefinementClearRefinement
        className="hidden self-start desktop:inline-flex"
        includedAttributes={[attribute]}
      />
    </div>
  )
}
